import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    Pagination,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';

import { AlertColor } from '@mui/material/Alert';

import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import UpdateOutlinedIcon from '@mui/icons-material/UpdateOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';

import {
    IRequestPointProviderGoal,
    IRequestPointSettings,
    IRequestTypePoint,
} from '../../Models/RequestPointSettings/IRequestPointSettings';

import {
    ERROR,
    SUCCESS,
} from '../../utils/constants';

import { formatUtcDateTime } from '../../utils/timeFormat';

import {
    setIsLoading,
    setShowMessage,
} from '../../store/shared.slice';

import { useAppDispatch } from '../../store/store';

import AlertDialog from '../AlertDialog/AlertDialog';
import Loading from '../Shared/Loading';

import {
    getRequestPointSettings,
    resetRequestPointSettings,
    saveRequestPointSettings,
} from './requestPointSettingsService';
import { getRequestTypeIcon } from './requestTypeIconHelper';

const PROVIDER_PAGE_SIZE = 6;
const REQUEST_PAGE_SIZE = 8;

const isHalfIncrement = (value: number) =>
    Number.isFinite(value) &&
    Math.round(value * 2) === value * 2;

const parseNumber = (value: string) =>
    value === ''
        ? Number.NaN
        : Number(value);

const numberFieldValue = (value: number) =>
    Number.isNaN(value)
        ? ''
        : value;

const getErrorMessage = (
    error: any,
    fallback: string
) => {
    if (
        typeof error?.response?.data ===
        'string'
    ) {
        return error.response.data;
    }

    if (error?.response?.data?.error) {
        return error.response.data.error;
    }

    if (error?.response?.data?.message) {
        return error.response.data.message;
    }

    if (error?.message) {
        return error.message;
    }

    return fallback;
};

const validateSettings = (
    settings: IRequestPointSettings
): string | null => {
    if (
        !Number.isFinite(
            settings.globalDailyGoal
        ) ||
        settings.globalDailyGoal <= 0
    ) {
        return 'Global daily goal must be greater than zero.';
    }

    if (
        !isHalfIncrement(
            settings.globalDailyGoal
        )
    ) {
        return 'Global daily goal must use 0.5 increments.';
    }

    const providerIds =
        new Set<string>();

    for (
        const providerGoal
        of settings.providerGoals
    ) {
        const providerId =
            providerGoal.providerId?.trim();

        if (!providerId) {
            return 'Provider identifier is required.';
        }

        if (
            providerIds.has(providerId)
        ) {
            return 'Duplicate provider goals are not allowed.';
        }

        providerIds.add(providerId);

        if (
            !Number.isFinite(
                providerGoal.goal
            ) ||
            providerGoal.goal < 0
        ) {
            return 'Provider goals cannot be negative.';
        }

        if (
            !isHalfIncrement(
                providerGoal.goal
            )
        ) {
            return 'Provider goals must use 0.5 increments.';
        }
    }

    const requestTypeKeys =
        new Set<string>();

    for (
        const requestPoint
        of settings.requestPoints
    ) {
        const requestType =
            requestPoint
                .requestType
                ?.trim();

        const requestTypeId =
            requestPoint
                .requestTypeId
                ?.trim();

        if (!requestType) {
            return 'Request type is required.';
        }

        const requestTypeKey =
            (
                requestTypeId ||
                requestType
            ).toLowerCase();

        if (
            requestTypeKeys.has(
                requestTypeKey
            )
        ) {
            return 'Duplicate request type point values are not allowed.';
        }

        requestTypeKeys.add(
            requestTypeKey
        );

        if (
            !Number.isFinite(
                requestPoint.points
            ) ||
            requestPoint.points < 0
        ) {
            return 'Request points cannot be negative.';
        }

        if (
            !isHalfIncrement(
                requestPoint.points
            )
        ) {
            return 'Request points must use 0.5 increments.';
        }
    }

    return null;
};

const getInitials = (
    name: string
) => {
    if (!name) {
        return '';
    }

    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) =>
            part.charAt(0).toUpperCase()
        )
        .join('');
};

const RequestPointSettings = (
    props: any
) => {
    const dispatch =
        useAppDispatch();

    const [
        settings,
        setSettings,
    ] =
        useState<IRequestPointSettings | null>(
            null
        );

    const [
        providerSearch,
        setProviderSearch,
    ] =
        useState('');

    const [
        requestSearch,
        setRequestSearch,
    ] =
        useState('');

    const [
        providerPage,
        setProviderPage,
    ] =
        useState(1);

    const [
        requestPage,
        setRequestPage,
    ] =
        useState(1);

    const [
        validationMessage,
        setValidationMessage,
    ] =
        useState('');

    const [
        saving,
        setSaving,
    ] =
        useState(false);

    const [
        resetting,
        setResetting,
    ] =
        useState(false);

    const [
        showResetConfirm,
        setShowResetConfirm,
    ] =
        useState(false);

    document.title =
        props.title ||
        'CHP - Request Point Settings';

    const showMessage =
        useCallback(
            (
                message: string,
                messageType:
                    AlertColor
            ) => {
                dispatch(
                    setShowMessage(
                        true,
                        message,
                        messageType
                    )
                );
            },
            [dispatch]
        );

    const loadSettings =
        useCallback(
            async () => {
                dispatch(
                    setIsLoading(true)
                );

                setValidationMessage('');

                try {
                    const response =
                        await getRequestPointSettings();

                    setSettings(response);
                } catch (error) {
                    showMessage(
                        getErrorMessage(
                            error,
                            'Unable to load request point settings.'
                        ),
                        ERROR as AlertColor
                    );
                } finally {
                    dispatch(
                        setIsLoading(
                            false
                        )
                    );
                }
            },
            [
                dispatch,
                showMessage,
            ]
        );

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    const filteredProviderGoals =
        useMemo(() => {
            if (!settings) {
                return [];
            }

            const search =
                providerSearch
                    .trim()
                    .toLowerCase();

            return settings
                .providerGoals
                .filter(
                    (goal) =>
                        !search ||
                        goal
                            .providerName
                            .toLowerCase()
                            .includes(
                                search
                            )
                );
        }, [
            providerSearch,
            settings,
        ]);

    const filteredRequestPoints =
        useMemo(() => {
            if (!settings) {
                return [];
            }

            const search =
                requestSearch
                    .trim()
                    .toLowerCase();

            return settings
                .requestPoints
                .filter(
                    (point) =>
                        !search ||
                        point
                            .requestType
                            .toLowerCase()
                            .includes(
                                search
                            )
                );
        }, [
            requestSearch,
            settings,
        ]);

    useEffect(() => {
        setProviderPage(1);
    }, [providerSearch]);

    useEffect(() => {
        setRequestPage(1);
    }, [requestSearch]);

    const providerPageCount =
        Math.max(
            1,
            Math.ceil(
                filteredProviderGoals.length /
                    PROVIDER_PAGE_SIZE
            )
        );

    const requestPageCount =
        Math.max(
            1,
            Math.ceil(
                filteredRequestPoints.length /
                    REQUEST_PAGE_SIZE
            )
        );

    const paginatedProviders =
        useMemo(() => {
            const start =
                (providerPage - 1) *
                PROVIDER_PAGE_SIZE;

            return filteredProviderGoals.slice(
                start,
                start +
                    PROVIDER_PAGE_SIZE
            );
        }, [
            filteredProviderGoals,
            providerPage,
        ]);

    const paginatedRequestPoints =
        useMemo(() => {
            const start =
                (requestPage - 1) *
                REQUEST_PAGE_SIZE;

            return filteredRequestPoints.slice(
                start,
                start +
                    REQUEST_PAGE_SIZE
            );
        }, [
            filteredRequestPoints,
            requestPage,
        ]);

    const updateGlobalDailyGoal = (value: string) => {
        setSettings((current) =>
            current
                ? {
                      ...current,
                      globalDailyGoal: parseNumber(value),
                  }
                : current
        );
    };

    const splitGoalEvenly = () => {
        setSettings((current) => {
            if (!current || !current.providerGoals.length) {
                return current;
            }

            if (
                !Number.isFinite(current.globalDailyGoal) ||
                current.globalDailyGoal <= 0 ||
                !isHalfIncrement(current.globalDailyGoal)
            ) {
                return current;
            }

            // Convert the total to half-point units so every provider goal
            // remains compatible with the existing 0.5 increment rule.
            const totalHalfUnits = Math.round(current.globalDailyGoal * 2);
            const providerCount = current.providerGoals.length;
            const baseHalfUnits = Math.floor(totalHalfUnits / providerCount);
            const remainingHalfUnits = totalHalfUnits % providerCount;

            const providerGoals = current.providerGoals.map((providerGoal, index) => ({
                ...providerGoal,
                goal: (baseHalfUnits + (index < remainingHalfUnits ? 1 : 0)) / 2,
            }));

            return {
                ...current,
                providerGoals,
            };
        });

        setValidationMessage('');
    };

    const updateProviderGoal = (providerId: string, value: string) => {
        setSettings((current) => {
            if (!current) {
                return current;
            }

            const providerGoals = current.providerGoals.map((providerGoal) =>
                providerGoal.providerId === providerId
                    ? {
                          ...providerGoal,
                          goal: parseNumber(value),
                      }
                    : providerGoal
            );

            const hasInvalidGoal = providerGoals.some(
                (providerGoal) => !Number.isFinite(providerGoal.goal)
            );

            const globalDailyGoal = hasInvalidGoal
                ? Number.NaN
                : providerGoals.reduce(
                      (total, providerGoal) => total + providerGoal.goal,
                      0
                  );

            return {
                ...current,
                globalDailyGoal,
                providerGoals,
            };
        });
    };

    const updateRequestPoint =
        (
            requestPoint:
                IRequestTypePoint,
            value: string
        ) => {
            setSettings(
                (current) =>
                    current
                        ? {
                              ...current,
                              requestPoints:
                                  current
                                      .requestPoints
                                      .map(
                                          (
                                              point
                                          ) =>
                                              point.requestTypeId ===
                                                  requestPoint.requestTypeId &&
                                              point.requestType ===
                                                  requestPoint.requestType
                                                  ? {
                                                        ...point,
                                                        points: parseNumber(
                                                            value
                                                        ),
                                                    }
                                                  : point
                                      ),
                          }
                        : current
            );
        };

    const handleSave =
        async () => {
            if (!settings) {
                return;
            }

            const validationError =
                validateSettings(
                    settings
                );

            if (validationError) {
                setValidationMessage(
                    validationError
                );

                showMessage(
                    validationError,
                    ERROR as AlertColor
                );

                return;
            }

            setSaving(true);
            setValidationMessage('');

            try {
                const response =
                    await saveRequestPointSettings(
                        settings
                    );

                setSettings(response);

                showMessage(
                    'Request point settings saved successfully.',
                    SUCCESS as AlertColor
                );
            } catch (error) {
                showMessage(
                    getErrorMessage(
                        error,
                        'Unable to save request point settings.'
                    ),
                    ERROR as AlertColor
                );
            } finally {
                setSaving(false);
            }
        };

    const handleReset =
        async () => {
            setResetting(true);

            setValidationMessage('');

            try {
                const response =
                    await resetRequestPointSettings();

                setSettings(response);

                setShowResetConfirm(
                    false
                );

                setProviderPage(1);
                setRequestPage(1);

                showMessage(
                    'Request point settings reset successfully.',
                    SUCCESS as AlertColor
                );
            } catch (error) {
                showMessage(
                    getErrorMessage(
                        error,
                        'Unable to reset request point settings.'
                    ),
                    ERROR as AlertColor
                );
            } finally {
                setResetting(false);
            }
        };

    const renderActions =
        () => (
            <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{
                    flexShrink: 0,
                    flexWrap: 'nowrap',
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={
                        resetting ? (
                            <CircularProgress
                                size={17}
                                color="inherit"
                            />
                        ) : (
                            <RestartAltOutlinedIcon />
                        )
                    }
                    onClick={() =>
                        setShowResetConfirm(
                            true
                        )
                    }
                    disabled={
                        !settings ||
                        saving ||
                        resetting
                    }
                    sx={{
                        minWidth: 155,
                        height: 40,
                        borderRadius: 1.5,
                        textTransform:
                            'none',
                        fontWeight: 600,
                        whiteSpace:
                            'nowrap',
                    }}
                >
                    {resetting
                        ? 'Resetting...'
                        : 'Reset to Defaults'}
                </Button>

                <Button
                    variant="contained"
                    startIcon={
                        saving ? (
                            <CircularProgress
                                size={17}
                                color="inherit"
                            />
                        ) : (
                            <SaveOutlinedIcon />
                        )
                    }
                    onClick={
                        handleSave
                    }
                    disabled={
                        !settings ||
                        saving ||
                        resetting
                    }
                    sx={{
                        minWidth: 150,
                        height: 40,
                        borderRadius: 1.5,
                        textTransform:
                            'none',
                        fontWeight: 600,
                        whiteSpace:
                            'nowrap',
                    }}
                >
                    {saving
                        ? 'Saving...'
                        : 'Save Changes'}
                </Button>
            </Stack>
        );

    return (
        <Box
            sx={{
                p: {
                    xs: 2,
                    md: 3,
                },

                bgcolor:
                    '#f8fafc',

                minHeight:
                    '100%',
            }}
        >
            <Loading message="Loading..." />

            <AlertDialog
                open={
                    showResetConfirm
                }
                onClose={() =>
                    setShowResetConfirm(
                        false
                    )
                }
                onConfirm={
                    handleReset
                }
                title="Reset Request Point Settings"
                message="This will restore the global daily goal, provider goals, and request type point values to the current defaults."
                okText="Reset"
                okButtonColor="warning"
                isLoading={
                    resetting
                }
            />

            <Stack spacing={2}>

                {/* ================= HEADER ================= */}

                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        borderColor:
                            '#e2e8f0',
                        overflow:
                            'hidden',
                        bgcolor:
                            '#ffffff',
                    }}
                >
                    <Box
                        sx={{
                            px: {
                                xs: 2,
                                md: 3,
                            },

                            py: 2.25,
                        }}
                    >
                        <Stack
                            direction={{
                                xs: 'column',
                                md: 'row',
                            }}
                            justifyContent="space-between"
                            alignItems={{
                                xs: 'stretch',
                                md: 'center',
                            }}
                            spacing={2}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1.5}
                            >
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,

                                        borderRadius:
                                            2,

                                        bgcolor:
                                            '#eff6ff',

                                        color:
                                            '#2563eb',

                                        display:
                                            'flex',

                                        alignItems:
                                            'center',

                                        justifyContent:
                                            'center',

                                        flexShrink:
                                            0,
                                    }}
                                >
                                    <SettingsOutlinedIcon />
                                </Box>

                                <Box>
                                    <Typography
                                        variant="h5"
                                        component="h1"
                                        fontWeight={
                                            700
                                        }
                                        color="#0f172a"
                                    >
                                        SNC Points Settings
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        mt={0.35}
                                    >
                                        Manage daily points goal and points per request type for all SNC providers.
                                    </Typography>
                                </Box>
                            </Stack>

                            {renderActions()}
                        </Stack>
                    </Box>

                    <Divider />

                    {/* ================= SUMMARY ================= */}

                    <Box
                        sx={{
                            px: {
                                xs: 2,
                                md: 3,
                            },

                            py: 1.75,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                                overflowX:
                                    'auto',

                                '&::-webkit-scrollbar':
                                    {
                                        height:
                                            0,
                                    },
                            }}
                        >

                            {/* TOTAL DAILY GOAL */}

                            <Card
                                variant="outlined"
                                sx={{
                                    minWidth:
                                        210,

                                    borderRadius:
                                        2,

                                    borderColor:
                                        '#e2e8f0',

                                    boxShadow:
                                        '0 1px 3px rgba(15, 23, 42, 0.05)',
                                }}
                            >
                                <CardContent
                                    sx={{
                                        py: '12px !important',
                                        px: 1.75,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                    >
                                        <Box
                                            sx={{
                                                width:
                                                    44,

                                                height:
                                                    44,

                                                borderRadius:
                                                    2,

                                                bgcolor:
                                                    '#f3e8ff',

                                                color:
                                                    '#7c3aed',

                                                display:
                                                    'flex',

                                                alignItems:
                                                    'center',

                                                justifyContent:
                                                    'center',
                                            }}
                                        >
                                            <TrackChangesOutlinedIcon />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Total Daily Goal
                                            </Typography>

                                            <Typography
                                                variant="h6"
                                                fontWeight={
                                                    700
                                                }
                                                color="#7c3aed"
                                                lineHeight={
                                                    1.2
                                                }
                                            >
                                                {numberFieldValue(
                                                    settings
                                                        ?.globalDailyGoal ??
                                                        0
                                                )}{' '}
                                                pts
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* TOTAL PROVIDERS */}

                            <Card
                                variant="outlined"
                                sx={{
                                    minWidth:
                                        200,

                                    borderRadius:
                                        2,

                                    borderColor:
                                        '#e2e8f0',

                                    boxShadow:
                                        '0 1px 3px rgba(15, 23, 42, 0.05)',
                                }}
                            >
                                <CardContent
                                    sx={{
                                        py: '12px !important',
                                        px: 1.75,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                    >
                                        <Box
                                            sx={{
                                                width:
                                                    44,

                                                height:
                                                    44,

                                                borderRadius:
                                                    2,

                                                bgcolor:
                                                    '#dcfce7',

                                                color:
                                                    '#16a34a',

                                                display:
                                                    'flex',

                                                alignItems:
                                                    'center',

                                                justifyContent:
                                                    'center',
                                            }}
                                        >
                                            <GroupsOutlinedIcon />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Total Providers
                                            </Typography>

                                            <Typography
                                                variant="h6"
                                                fontWeight={
                                                    700
                                                }
                                                color="#16a34a"
                                                lineHeight={
                                                    1.2
                                                }
                                            >
                                                {settings
                                                    ?.providerGoals
                                                    .length ??
                                                    0}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>

                            {/* REQUEST TYPES */}

                            <Card
                                variant="outlined"
                                sx={{
                                    minWidth:
                                        200,

                                    borderRadius:
                                        2,

                                    borderColor:
                                        '#e2e8f0',

                                    boxShadow:
                                        '0 1px 3px rgba(15, 23, 42, 0.05)',
                                }}
                            >
                                <CardContent
                                    sx={{
                                        py: '12px !important',
                                        px: 1.75,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                    >
                                        <Box
                                            sx={{
                                                width:
                                                    44,

                                                height:
                                                    44,

                                                borderRadius:
                                                    2,

                                                bgcolor:
                                                    '#e0f2fe',

                                                color:
                                                    '#0284c7',

                                                display:
                                                    'flex',

                                                alignItems:
                                                    'center',

                                                justifyContent:
                                                    'center',
                                            }}
                                        >
                                            <DescriptionOutlinedIcon />
                                        </Box>

                                        <Box>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                            >
                                                Request Types
                                            </Typography>

                                            <Typography
                                                variant="h6"
                                                fontWeight={
                                                    700
                                                }
                                                color="#0284c7"
                                                lineHeight={
                                                    1.2
                                                }
                                            >
                                                {settings
                                                    ?.requestPoints
                                                    .length ??
                                                    0}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Stack>
                    </Box>
                </Paper>

                {/* ================= VALIDATION ================= */}

                {validationMessage ? (
                    <Alert
                        severity="error"
                        onClose={() =>
                            setValidationMessage(
                                ''
                            )
                        }
                    >
                        {
                            validationMessage
                        }
                    </Alert>
                ) : null}

                {/* ================= MAIN CONTENT ================= */}

                <Grid
                    container
                    spacing={2}
                    alignItems="stretch"
                >

                    {/* ================= GLOBAL DAILY GOAL ================= */}

                    <Grid
                        size={{
                            xs: 12,
                            lg: 3,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                height:
                                    '100%',

                                borderRadius:
                                    2,

                                borderColor:
                                    '#e2e8f0',

                                overflow:
                                    'hidden',

                                bgcolor:
                                    '#ffffff',
                            }}
                        >
                            <Box
                                sx={{
                                    px: 2.25,
                                    py: 1.75,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                >
                                    <Box
                                        sx={{
                                            width:
                                                32,

                                            height:
                                                32,

                                            borderRadius:
                                                1.5,

                                            bgcolor:
                                                '#eff6ff',

                                            color:
                                                '#2563eb',

                                            display:
                                                'flex',

                                            alignItems:
                                                'center',

                                            justifyContent:
                                                'center',
                                        }}
                                    >
                                        <TrackChangesOutlinedIcon
                                            sx={{
                                                fontSize:
                                                    19,
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="subtitle1"
                                        fontWeight={
                                            700
                                        }
                                    >
                                        Global Daily Goal
                                    </Typography>

                                    <InfoOutlinedIcon
                                        sx={{
                                            color:
                                                '#2563eb',

                                            fontSize:
                                                17,
                                        }}
                                    />
                                </Stack>
                            </Box>

                            <Divider />

                            <Box
                                sx={{
                                    p: 2.25,
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    mb={0.75}
                                >
                                    Total Daily Points Goal
                                </Typography>

                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    value={numberFieldValue(
                                        settings?.globalDailyGoal ?? Number.NaN
                                    )}
                                    onChange={(event) =>
                                        updateGlobalDailyGoal(event.target.value)
                                    }
                                    inputProps={{
                                        min: 0.5,
                                        step: 0.5,
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <FlagOutlinedIcon
                                                    sx={{
                                                        color: '#7c3aed',
                                                        fontSize: 18,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                pts
                                            </InputAdornment>
                                        ),
                                    }}
                                    disabled={!settings || saving || resetting}
                                />

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<GroupsOutlinedIcon />}
                                    onClick={splitGoalEvenly}
                                    disabled={
                                        !settings ||
                                        !settings.providerGoals.length ||
                                        !Number.isFinite(settings.globalDailyGoal) ||
                                        settings.globalDailyGoal <= 0 ||
                                        !isHalfIncrement(settings.globalDailyGoal) ||
                                        saving ||
                                        resetting
                                    }
                                    sx={{
                                        mt: 1.25,
                                        height: 38,
                                        borderRadius: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    Split Evenly
                                </Button>

                                {/* INFO */}

                                <Box
                                    sx={{
                                        mt: 2.5,

                                        p: 1.75,

                                        borderRadius:
                                            1.5,

                                        bgcolor:
                                            '#eff6ff',

                                        border:
                                            '1px solid #bfdbfe',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        alignItems="flex-start"
                                    >
                                        <InfoOutlinedIcon
                                            sx={{
                                                color:
                                                    '#2563eb',

                                                fontSize:
                                                    20,

                                                mt: '1px',

                                                flexShrink:
                                                    0,
                                            }}
                                        />

                                        <Typography
                                            variant="caption"
                                            color="#2563eb"
                                            lineHeight={
                                                1.6
                                            }
                                        >
                                            Enter the total daily goal and select Split Evenly to distribute it across all providers. If a provider goal is changed manually, the total daily goal is recalculated automatically.
                                        </Typography>
                                    </Stack>
                                </Box>

                                <Divider
                                    sx={{
                                        my: 2.5,
                                    }}
                                />

                                {/* LAST UPDATED */}

                                <Stack
                                    direction="row"
                                    alignItems="flex-start"
                                    spacing={1}
                                >
                                    <Box
                                        sx={{
                                            width:
                                                30,

                                            height:
                                                30,

                                            borderRadius:
                                                1.5,

                                            bgcolor:
                                                '#f1f5f9',

                                            color:
                                                '#64748b',

                                            display:
                                                'flex',

                                            alignItems:
                                                'center',

                                            justifyContent:
                                                'center',

                                            flexShrink:
                                                0,
                                        }}
                                    >
                                        <UpdateOutlinedIcon
                                            sx={{
                                                fontSize:
                                                    18,
                                            }}
                                        />
                                    </Box>

                                    <Box>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            display="block"
                                        >
                                            Last Updated
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            fontWeight={
                                                500
                                            }
                                            mt={0.25}
                                        >
                                            {settings
                                                ?.updatedOn
                                                ? formatUtcDateTime(
                                                      settings.updatedOn
                                                  )
                                                : 'Not yet updated'}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {settings
                                                ?.updatedBy
                                                ? `Updated by ${settings.updatedBy}`
                                                : 'Updated by system defaults'}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Paper>
                    </Grid>

                    {/* ================= REQUEST TYPE POINTS ================= */}

                    <Grid
                        size={{
                            xs: 12,
                            lg: 5,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                height:
                                    '100%',

                                borderRadius:
                                    2,

                                borderColor:
                                    '#e2e8f0',

                                overflow:
                                    'hidden',

                                display:
                                    'flex',

                                flexDirection:
                                    'column',

                                bgcolor:
                                    '#ffffff',
                            }}
                        >
                            {/* HEADER */}

                            <Box
                                sx={{
                                    px: 2.25,
                                    py: 1.6,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{
                                        flexWrap:
                                            'nowrap',
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            minWidth:
                                                0,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width:
                                                    32,

                                                height:
                                                    32,

                                                borderRadius:
                                                    1.5,

                                                bgcolor:
                                                    '#f5f3ff',

                                                color:
                                                    '#7c3aed',

                                                display:
                                                    'flex',

                                                alignItems:
                                                    'center',

                                                justifyContent:
                                                    'center',

                                                flexShrink:
                                                    0,
                                            }}
                                        >
                                            <DescriptionOutlinedIcon
                                                sx={{
                                                    fontSize:
                                                        19,
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={
                                                700
                                            }
                                            noWrap
                                        >
                                            Points Per Request Type
                                        </Typography>

                                        <InfoOutlinedIcon
                                            sx={{
                                                color:
                                                    '#7c3aed',

                                                fontSize:
                                                    17,

                                                flexShrink:
                                                    0,
                                            }}
                                        />
                                    </Stack>

                                    <TextField
                                        size="small"
                                        placeholder="Search request type..."
                                        value={
                                            requestSearch
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setRequestSearch(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <SearchOutlinedIcon
                                                            sx={{
                                                                fontSize:
                                                                    18,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                        }}
                                        sx={{
                                            width:
                                                220,

                                            flexShrink:
                                                0,
                                        }}
                                    />
                                </Stack>
                            </Box>

                            <Divider />

                            {/* TABLE */}

                            <TableContainer
                                sx={{
                                    flex: 1,
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                bgcolor:
                                                    '#f8fafc',
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    fontWeight:
                                                        600,

                                                    color:
                                                        '#475569',
                                                }}
                                            >
                                                Request Type
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    width:
                                                        135,

                                                    fontWeight:
                                                        600,

                                                    color:
                                                        '#475569',
                                                }}
                                            >
                                                Points
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {paginatedRequestPoints.map(
                                            (
                                                point
                                            ) => {
                                                const requestIcon =
                                                    getRequestTypeIcon(
                                                        point.requestType
                                                    );

                                                return (
                                                    <TableRow
                                                        key={`${point.requestTypeId}-${point.requestType}`}
                                                        hover
                                                        sx={{
                                                            '& td':
                                                                {
                                                                    py: 1.1,
                                                                    borderColor:
                                                                        '#f1f5f9',
                                                                },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={
                                                                    1.25
                                                                }
                                                                sx={{
                                                                    minWidth:
                                                                        0,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        width:
                                                                            34,

                                                                        height:
                                                                            34,

                                                                        borderRadius:
                                                                            1.5,

                                                                        bgcolor:
                                                                            requestIcon.background,

                                                                        color:
                                                                            requestIcon.color,

                                                                        display:
                                                                            'flex',

                                                                        alignItems:
                                                                            'center',

                                                                        justifyContent:
                                                                            'center',

                                                                        flexShrink:
                                                                            0,
                                                                    }}
                                                                >
                                                                    {
                                                                        requestIcon.icon
                                                                    }
                                                                </Box>

                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={
                                                                        500
                                                                    }
                                                                    noWrap
                                                                    title={
                                                                        point.requestType
                                                                    }
                                                                    sx={{
                                                                        color:
                                                                            '#334155',
                                                                    }}
                                                                >
                                                                    {
                                                                        point.requestType
                                                                    }
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={numberFieldValue(
                                                                    point.points
                                                                )}
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    updateRequestPoint(
                                                                        point,
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                inputProps={{
                                                                    min: 0,
                                                                    step: 0.5,
                                                                }}
                                                                InputProps={{
                                                                    endAdornment:
                                                                        (
                                                                            <InputAdornment position="end">
                                                                                pts
                                                                            </InputAdornment>
                                                                        ),
                                                                }}
                                                                disabled={
                                                                    saving ||
                                                                    resetting
                                                                }
                                                                sx={{
                                                                    width:
                                                                        115,
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }
                                        )}

                                        {!paginatedRequestPoints.length ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        2
                                                    }
                                                    align="center"
                                                    sx={{
                                                        py: 5,
                                                    }}
                                                >
                                                    <Stack
                                                        alignItems="center"
                                                        spacing={
                                                            1
                                                        }
                                                    >
                                                        <SearchOutlinedIcon
                                                            sx={{
                                                                color:
                                                                    '#94a3b8',
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            No request types match the current search.
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Divider />

                            {/* FOOTER */}

                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                    px: 2,
                                    py: 1.4,
                                    minHeight:
                                        58,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={
                                        0.75
                                    }
                                >
                                    <DescriptionOutlinedIcon
                                        sx={{
                                            fontSize:
                                                16,

                                            color:
                                                '#7c3aed',
                                        }}
                                    />

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        whiteSpace="nowrap"
                                    >
                                        {
                                            filteredRequestPoints.length
                                        }{' '}
                                        request types
                                    </Typography>
                                </Stack>

                                {requestPageCount >
                                1 ? (
                                    <Pagination
                                        page={
                                            requestPage
                                        }
                                        count={
                                            requestPageCount
                                        }
                                        size="small"
                                        color="primary"
                                        onChange={(
                                            _,
                                            page
                                        ) =>
                                            setRequestPage(
                                                page
                                            )
                                        }
                                    />
                                ) : null}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* ================= PROVIDER GOALS ================= */}

                    <Grid
                        size={{
                            xs: 12,
                            lg: 4,
                        }}
                    >
                        <Paper
                            variant="outlined"
                            sx={{
                                height:
                                    '100%',

                                borderRadius:
                                    2,

                                borderColor:
                                    '#e2e8f0',

                                overflow:
                                    'hidden',

                                display:
                                    'flex',

                                flexDirection:
                                    'column',

                                bgcolor:
                                    '#ffffff',
                            }}
                        >
                            {/* HEADER */}

                            <Box
                                sx={{
                                    px: 2.25,
                                    py: 1.6,
                                }}
                            >
                                <Stack
                                    spacing={1.5}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                    >
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            <Box
                                                sx={{
                                                    width:
                                                        32,

                                                    height:
                                                        32,

                                                    borderRadius:
                                                        1.5,

                                                    bgcolor:
                                                        '#ecfdf5',

                                                    color:
                                                        '#16a34a',

                                                    display:
                                                        'flex',

                                                    alignItems:
                                                        'center',

                                                    justifyContent:
                                                        'center',
                                                }}
                                            >
                                                <GroupsOutlinedIcon
                                                    sx={{
                                                        fontSize:
                                                            19,
                                                    }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={
                                                    700
                                                }
                                                noWrap
                                            >
                                                Provider Goals
                                            </Typography>
                                        </Stack>

                                        <Chip
                                            icon={
                                                <GroupsOutlinedIcon />
                                            }
                                            label={`${settings?.providerGoals.length ?? 0} Providers`}
                                            size="small"
                                            sx={{
                                                bgcolor:
                                                    '#f0fdf4',

                                                color:
                                                    '#15803d',

                                                border:
                                                    '1px solid #bbf7d0',

                                                '& .MuiChip-icon':
                                                    {
                                                        color:
                                                            '#16a34a',
                                                    },
                                            }}
                                        />
                                    </Stack>

                                    <TextField
                                        size="small"
                                        placeholder="Search provider..."
                                        value={
                                            providerSearch
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setProviderSearch(
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                        InputProps={{
                                            startAdornment:
                                                (
                                                    <InputAdornment position="start">
                                                        <SearchOutlinedIcon
                                                            sx={{
                                                                fontSize:
                                                                    18,
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                        }}
                                        fullWidth
                                    />
                                </Stack>
                            </Box>

                            <Divider />

                            {/* TABLE */}

                            <TableContainer
                                sx={{
                                    flex: 1,
                                }}
                            >
                                <Table size="small">
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                bgcolor:
                                                    '#f8fafc',
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    fontWeight:
                                                        600,

                                                    color:
                                                        '#475569',
                                                }}
                                            >
                                                Provider
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    width:
                                                        140,

                                                    fontWeight:
                                                        600,

                                                    color:
                                                        '#475569',
                                                }}
                                            >
                                                Daily Goal
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {paginatedProviders.map(
                                            (
                                                goal:
                                                    IRequestPointProviderGoal,
                                                index
                                            ) => {
                                                const providerColors =
                                                    [
                                                        {
                                                            bg: '#f3e8ff',
                                                            color:
                                                                '#7c3aed',
                                                        },
                                                        {
                                                            bg: '#dbeafe',
                                                            color:
                                                                '#2563eb',
                                                        },
                                                        {
                                                            bg: '#dcfce7',
                                                            color:
                                                                '#16a34a',
                                                        },
                                                        {
                                                            bg: '#cffafe',
                                                            color:
                                                                '#0891b2',
                                                        },
                                                        {
                                                            bg: '#ffedd5',
                                                            color:
                                                                '#ea580c',
                                                        },
                                                        {
                                                            bg: '#fce7f3',
                                                            color:
                                                                '#db2777',
                                                        },
                                                    ];

                                                const avatarColor =
                                                    providerColors[
                                                        index %
                                                            providerColors.length
                                                    ];

                                                return (
                                                    <TableRow
                                                        key={
                                                            goal.providerId
                                                        }
                                                        hover
                                                        sx={{
                                                            '& td':
                                                                {
                                                                    py: 1.15,
                                                                    borderColor:
                                                                        '#f1f5f9',
                                                                },
                                                        }}
                                                    >
                                                        <TableCell>
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={
                                                                    1.1
                                                                }
                                                                sx={{
                                                                    minWidth:
                                                                        0,
                                                                }}
                                                            >
                                                                <Avatar
                                                                    sx={{
                                                                        width:
                                                                            34,

                                                                        height:
                                                                            34,

                                                                        fontSize:
                                                                            11,

                                                                        fontWeight:
                                                                            700,

                                                                        bgcolor:
                                                                            avatarColor.bg,

                                                                        color:
                                                                            avatarColor.color,

                                                                        flexShrink:
                                                                            0,
                                                                    }}
                                                                >
                                                                    {getInitials(
                                                                        goal.providerName
                                                                    )}
                                                                </Avatar>

                                                                <Typography
                                                                    variant="body2"
                                                                    fontWeight={
                                                                        500
                                                                    }
                                                                    noWrap
                                                                    title={
                                                                        goal.providerName
                                                                    }
                                                                    sx={{
                                                                        color:
                                                                            '#334155',
                                                                    }}
                                                                >
                                                                    {
                                                                        goal.providerName
                                                                    }
                                                                </Typography>
                                                            </Stack>
                                                        </TableCell>

                                                        <TableCell align="right">
                                                            <TextField
                                                                type="number"
                                                                size="small"
                                                                value={numberFieldValue(
                                                                    goal.goal
                                                                )}
                                                                onChange={(
                                                                    event
                                                                ) =>
                                                                    updateProviderGoal(
                                                                        goal.providerId,
                                                                        event
                                                                            .target
                                                                            .value
                                                                    )
                                                                }
                                                                inputProps={{
                                                                    min: 0,
                                                                    step: 0.5,
                                                                }}
                                                                InputProps={{
                                                                    endAdornment:
                                                                        (
                                                                            <InputAdornment position="end">
                                                                                pts
                                                                            </InputAdornment>
                                                                        ),
                                                                }}
                                                                disabled={
                                                                    saving ||
                                                                    resetting
                                                                }
                                                                sx={{
                                                                    width:
                                                                        115,
                                                                }}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }
                                        )}

                                        {!paginatedProviders.length ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={
                                                        2
                                                    }
                                                    align="center"
                                                    sx={{
                                                        py: 5,
                                                    }}
                                                >
                                                    <Stack
                                                        alignItems="center"
                                                        spacing={
                                                            1
                                                        }
                                                    >
                                                        <GroupsOutlinedIcon
                                                            sx={{
                                                                color:
                                                                    '#94a3b8',
                                                            }}
                                                        />

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            No providers match the current search.
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ) : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Divider />

                            {/* FOOTER */}

                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                                sx={{
                                    px: 2,
                                    py: 1.4,
                                    minHeight:
                                        58,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={
                                        0.75
                                    }
                                >
                                    <GroupsOutlinedIcon
                                        sx={{
                                            fontSize:
                                                16,

                                            color:
                                                '#16a34a',
                                        }}
                                    />

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        whiteSpace="nowrap"
                                    >
                                        {
                                            filteredProviderGoals.length
                                        }{' '}
                                        providers
                                    </Typography>
                                </Stack>

                                {providerPageCount >
                                1 ? (
                                    <Pagination
                                        page={
                                            providerPage
                                        }
                                        count={
                                            providerPageCount
                                        }
                                        size="small"
                                        color="primary"
                                        onChange={(
                                            _,
                                            page
                                        ) =>
                                            setProviderPage(
                                                page
                                            )
                                        }
                                    />
                                ) : null}
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>

                {/* ================= BOTTOM INFORMATION ================= */}

                <Alert
                    severity="info"
                    icon={
                        <InfoOutlinedIcon />
                    }
                    sx={{
                        borderRadius: 2,

                        border:
                            '1px solid #bfdbfe',

                        bgcolor:
                            '#eff6ff',

                        color:
                            '#1e40af',

                        '& .MuiAlert-icon':
                            {
                                color:
                                    '#2563eb',
                            },
                    }}
                >
                    Provider goals are configured for active Social Needs Coordinator providers. Request point values are global and apply to all providers.
                </Alert>
            </Stack>
        </Box>
    );
};

export default RequestPointSettings;
